/**
 * HubSpot integration service for tracking and contact management
 */

// HubSpot API client
interface HubspotContact {
  email: string;
  firstname?: string;
  lastname?: string;
  company?: string;
  jobtitle?: string;
  annual_carbon_credits?: string;
  [key: string]: string | undefined;
}

/**
 * Identifies a user in HubSpot and creates or updates their contact record
 * @param email User's email address
 * @param properties Additional contact properties
 */
const identifyContact = (
  email: string,
  properties: Record<string, string | boolean | number | undefined> = {}
) => {
  if (!email) return;

  try {
    // Access the HubSpot tracking code that was loaded in the HTML
    const _hsq = (window._hsq = window._hsq || []);

    // Identify the user with their email
    _hsq.push([
      'identify',
      {
        email,
        ...properties,
      },
    ]);

    // console.log('HubSpot: User identified', email);
  } catch (error) {
    // console.error('HubSpot: Error identifying user', error);
  }
};

/**
 * Creates or updates a contact in HubSpot via the API
 * @param contactData Contact data to send to HubSpot
 */
const createOrUpdateContact = async (contactData: HubspotContact) => {
  if (!contactData.email) {
    // console.error('HubSpot: Email is required to create/update a contact');
    return;
  }

  // This would typically use the HubSpot API directly
  // For this implementation, we're using the tracking code's identify method
  // In a production environment, you would make a server-side API call to HubSpot
  identifyContact(contactData.email, {
    firstname: contactData.firstname,
    lastname: contactData.lastname,
    company: contactData.company,
    jobtitle: contactData.jobtitle,
    annual_carbon_credits: contactData.annual_carbon_credits,
  });
};

/**
 * Tracks a page view in HubSpot
 * @param pageName Name of the page being viewed
 */
const trackPageView = (pageName?: string) => {
  try {
    const _hsq = (window._hsq = window._hsq || []);

    if (pageName) {
      _hsq.push(['setPath', window.location.pathname]);
      _hsq.push(['setContentType', pageName]);
    }

    _hsq.push(['trackPageView']);
  } catch (error) {
    // console.error('HubSpot: Error tracking page view', error);
  }
};

/**
 * Tracks a custom event in HubSpot
 * @param eventName Name of the event
 * @param properties Additional properties for the event
 */
const trackEvent = (eventName: string, properties: Record<string, unknown> = {}) => {
  try {
    const _hsq = (window._hsq = window._hsq || []);
    _hsq.push([
      'trackEvent',
      {
        id: eventName,
        value: properties,
      },
    ]);
  } catch (error) {
    // console.error('HubSpot: Error tracking event', error);
  }
};

const hubspotService = {
  identifyContact,
  createOrUpdateContact,
  trackPageView,
  trackEvent,
};

export default hubspotService;

// Add TypeScript declaration for HubSpot tracking code
declare global {
  interface Window {
    _hsq: unknown[];
  }
}
